import React, { useMemo, useState } from 'react'
import { IconDownload, IconEye } from '@tabler/icons-react'
import {
  ActionButton,
  ActionButtons,
  ActionButtonIcons,
  ACTION_BUTTON_DEFAULT_EDIT_VARIANT,
  ACTION_BUTTON_DEFAULT_VIEW_VARIANT,
  ACTION_BUTTON_SIZE_LABELS,
  ACTION_BUTTON_SIZES,
  ACTION_BUTTON_VARIANT_LABELS,
  ACTION_BUTTON_VARIANTS,
  type ActionButtonSize,
  type ActionButtonVariant,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './ActionButtonsShowcase.css'

const ActionButtonsShowcase: React.FC = () => {
  const [size, setSize] = useState<ActionButtonSize>('md')
  const [disabled, setDisabled] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [viewVariant, setViewVariant] = useState<ActionButtonVariant>(
    ACTION_BUTTON_DEFAULT_VIEW_VARIANT,
  )
  const [editVariant, setEditVariant] = useState<ActionButtonVariant>(
    ACTION_BUTTON_DEFAULT_EDIT_VARIANT,
  )

  const renderVariantSelect = (
    id: string,
    value: ActionButtonVariant,
    onChange: (variant: ActionButtonVariant) => void,
  ): React.ReactNode => (
    <select
      id={id}
      className="ui-field-control"
      value={value}
      onChange={(event) => onChange(event.target.value as ActionButtonVariant)}
    >
      {ACTION_BUTTON_VARIANTS.map((option) => (
        <option key={option} value={option}>
          {ACTION_BUTTON_VARIANT_LABELS[option]}
        </option>
      ))}
    </select>
  )

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Action set',
        fields: [
          {
            id: 'ab-size',
            label: 'Size',
            control: (
              <select
                id="ab-size"
                className="ui-field-control"
                value={size}
                onChange={(event) =>
                  setSize(event.target.value as ActionButtonSize)
                }
              >
                {ACTION_BUTTON_SIZES.map((option) => (
                  <option key={option} value={option}>
                    {ACTION_BUTTON_SIZE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'ab-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="ab-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'ab-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="ab-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'ab-view-variant',
            label: 'View variant',
            control: renderVariantSelect(
              'ab-view-variant',
              viewVariant,
              setViewVariant,
            ),
          },
          {
            id: 'ab-edit-variant',
            label: 'Edit variant',
            control: renderVariantSelect(
              'ab-edit-variant',
              editVariant,
              setEditVariant,
            ),
          },
        ],
      },
    ],
    [darkMode, disabled, editVariant, size, viewVariant],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Action buttons"
        description="Compact icon buttons for row-level actions (view, edit, delete) with an optional overflow menu. Used in the data table Action column."
        stageClassName="ui-preview__stage--wide action-buttons-showcase__stage-wrap"
      >
        <div
          className={`action-buttons-showcase__sheet${
            darkMode ? ' action-buttons-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Full action set: core actions, Billing icons, and overflow menu.
              Property controls apply to the configurable variants.
            </p>
            <div
              className={`action-buttons-showcase__live-stage${
                darkMode ? ' action-buttons-showcase__live-stage--dark' : ''
              }`}
            >
              <ActionButtons
                size={size}
                darkMode={darkMode}
                viewVariant={viewVariant}
                editVariant={editVariant}
                onView={() => {}}
                onEdit={() => {}}
                onDelete={() => {}}
                disableEdit={disabled}
                disableDelete={disabled}
                onPayment={() => {}}
                onPrint={() => {}}
                onRefund={() => {}}
                onCreateInvoice={() => {}}
                disablePayment={disabled}
                disableRefund={disabled}
                disableCreateInvoice={disabled}
                onViewNotes={() => {}}
                disableViewNotes={disabled}
                onDuplicate={() => {}}
                onPhone={() => {}}
                onMail={() => {}}
                onMessage={() => {}}
              />
            </div>
          </section>

          <section className="action-buttons-showcase__matrix">
            <h4 className="action-buttons-showcase__section-title">Variants</h4>
            <div className="action-buttons-showcase__grid">
              <div className="action-buttons-showcase__grid-row">
                <span className="action-buttons-showcase__grid-label">
                  Primary
                </span>
                {ACTION_BUTTON_VARIANTS.map((variantOption) => (
                  <ActionButton
                    key={variantOption}
                    icon={<IconEye size={16} stroke={1.8} />}
                    label={`Primary ${ACTION_BUTTON_VARIANT_LABELS[variantOption]}`}
                    tone="primary"
                    variant={variantOption}
                    darkMode={darkMode}
                  />
                ))}
              </div>
              <div className="action-buttons-showcase__grid-row">
                <span className="action-buttons-showcase__grid-label">
                  Delete
                </span>
                <ActionButton
                  icon={ActionButtonIcons.delete()}
                  label="Delete outline"
                  tone="danger"
                  variant="outline"
                  darkMode={darkMode}
                />
              </div>
            </div>
          </section>

          <section className="action-buttons-showcase__matrix">
            <h4 className="action-buttons-showcase__section-title">Recipes</h4>
            <div className="action-buttons-showcase__recipes">
              <div className="action-buttons-showcase__recipe">
                <span className="action-buttons-showcase__grid-label">
                  View / Edit / Delete
                </span>
                <ActionButtons
                  darkMode={darkMode}
                  onView={() => {}}
                  onEdit={() => {}}
                  onDelete={() => {}}
                />
              </div>
              <div className="action-buttons-showcase__recipe">
                <span className="action-buttons-showcase__grid-label">
                  With overflow menu
                </span>
                <ActionButtons
                  darkMode={darkMode}
                  onView={() => {}}
                  onEdit={() => {}}
                  onDelete={() => {}}
                  onDuplicate={() => {}}
                  onPhone={() => {}}
                  onMail={() => {}}
                  onMessage={() => {}}
                />
              </div>
              <div className="action-buttons-showcase__recipe">
                <span className="action-buttons-showcase__grid-label">
                  Disabled edit + delete
                </span>
                <ActionButtons
                  darkMode={darkMode}
                  onView={() => {}}
                  onEdit={() => {}}
                  onDelete={() => {}}
                  disableEdit
                  disableDelete
                />
              </div>
              <div className="action-buttons-showcase__recipe">
                <span className="action-buttons-showcase__grid-label">
                  Custom actions
                </span>
                <ActionButtons
                  darkMode={darkMode}
                  actions={[
                    {
                      id: 'download',
                      label: 'Download',
                      icon: <IconDownload size={16} stroke={1.8} />,
                      tone: 'primary',
                      variant: 'solid',
                      onClick: () => {},
                    },
                    {
                      id: 'remove',
                      label: 'Remove',
                      icon: ActionButtonIcons.delete(),
                      tone: 'danger',
                      variant: 'outline',
                      onClick: () => {},
                    },
                  ]}
                />
              </div>
              <div className="action-buttons-showcase__recipe">
                <span className="action-buttons-showcase__grid-label">
                  Billing — Invoice
                </span>
                <ActionButtons
                  darkMode={darkMode}
                  showView={false}
                  showEdit={false}
                  onPayment={() => {}}
                  onPrint={() => {}}
                  onDelete={() => {}}
                />
              </div>
              <div className="action-buttons-showcase__recipe">
                <span className="action-buttons-showcase__grid-label">
                  Billing — Ledger
                </span>
                <ActionButtons
                  darkMode={darkMode}
                  showEdit={false}
                  showDelete={false}
                  onView={() => {}}
                  onRefund={() => {}}
                  onCreateInvoice={() => {}}
                />
              </div>
              <div className="action-buttons-showcase__recipe">
                <span className="action-buttons-showcase__grid-label">
                  Billing — Payment outstanding (remove)
                </span>
                <ActionButtons
                  darkMode={darkMode}
                  showView={false}
                  showEdit={false}
                  onDelete={() => {}}
                />
              </div>
              <div className="action-buttons-showcase__recipe">
                <span className="action-buttons-showcase__grid-label">
                  Billing — Payment history (notes)
                </span>
                <ActionButtons
                  darkMode={darkMode}
                  showView={false}
                  showEdit={false}
                  showDelete={false}
                  onViewNotes={() => {}}
                />
              </div>
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Action buttons"
        sections={propertySections}
      />
    </>
  )
}

export default ActionButtonsShowcase
